// Cierre de período de inventario — Inventario → Operaciones → Cierre de período.
//
// Contabilidad sella sus asientos; esto sella el COSTO. Un movimiento con
// fecha vieja recalcula el costo promedio de todo lo que vino después —el
// promedio es acumulativo— y cambia el costo de ventas de meses ya
// presentados sin tocar un solo asiento del mes sellado.
//
// Cerrar el mes congela la valorización línea por línea. Eso es lo que
// convierte el kardex en un documento y no en una consulta que da distinto
// cada vez que se corre.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, Loader2, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Location, Modal, fieldCls, invRpc, invalidateInventario, locationsList, money, num, toNum } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Periodo {
  id: string; anio: number; mes: number; fecha_inicio: string; fecha_fin: string;
  estado: 'abierto' | 'cerrado'; n_lineas: number | null; valor_cierre: number | null;
  hash_cierre: string | null; cerrado_at: string | null;
  reabierto_at: string | null; motivo_reapertura: string | null; valor_hoy: number;
}
interface LineaVal {
  location_id: string; bodega: string; producto_id: string; codigo: string; producto: string;
  unidades: number; costo_promedio: number; valor: number;
  unidades_hoy: number; valor_hoy: number;
}

const MES = ['', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export function CierrePeriodo() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [ver, setVer] = useState<Periodo | null>(null);
  const [cerrar, setCerrar] = useState<{ anio: number; mes: number } | null>(null);

  const { data: periodos = [], isFetching } = useQuery<Periodo[]>({
    queryKey: ['inv-periodos', anio], queryFn: () => invRpc<Periodo[]>('inv_periodos', { _anio: anio }),
  });

  // El año se muestra completo aunque no existan filas: un mes sin fila es un
  // mes abierto, y hay que poder cerrarlo desde aquí.
  const meses = useMemo(() => {
    const max = anio === hoy.getFullYear() ? hoy.getMonth() + 1 : 12;
    return Array.from({ length: max }, (_, i) => {
      const m = i + 1;
      return periodos.find((p) => p.mes === m) ?? null;
    });
  }, [periodos, anio, hoy]);

  const reabrir = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      invRpc<{ mensaje: string }>('inv_periodo_reabrir', { _id: id, _motivo: motivo }),
    onSuccess: (r) => { toast.success(r.mensaje); void qc.invalidateQueries({ queryKey: ['inv-periodos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4 p-6">
      {dlg.ui}
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <CalendarCheck className="h-5 w-5" /> Cierre de período de inventario
        </h1>
        <p className="text-sm text-muted-foreground">
          Sellar un mes congela su valorización y ningún movimiento podrá volver a fecharse ahí.
          Es lo que impide que el costo de un mes ya declarado cambie por detrás.
        </p>
      </div>

      <Campo label="Año" className="w-32">
        <input type="number" className={fieldCls} value={anio} onChange={(e) => setAnio(toNum(e.target.value) || hoy.getFullYear())} />
      </Campo>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Meses de {anio}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Mes</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3 text-right">Líneas</th>
                <th className="py-2 pr-3 text-right">Valor sellado</th>
                <th className="py-2 pr-3">Sellado</th>
                <th className="py-2 pr-3">Hash</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {meses.map((p, i) => {
                const mes = i + 1;
                const enCurso = anio === hoy.getFullYear() && mes === hoy.getMonth() + 1;
                return (
                  <tr key={mes} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 capitalize">{MES[mes]}</td>
                    <td className="py-2 pr-3">
                      {p?.estado === 'cerrado'
                        ? <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"><Lock className="h-3 w-3" />Cerrado</span>
                        : enCurso
                          ? <span className="text-xs text-muted-foreground">en curso</span>
                          : <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"><Unlock className="h-3 w-3" />Abierto</span>}
                      {p?.reabierto_at && (
                        <span className="ml-2 text-[11px] text-amber-700 dark:text-amber-400" title={p.motivo_reapertura ?? ''}>
                          reabierto
                        </span>)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{p?.n_lineas != null ? num(p.n_lineas, 0) : '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{p?.valor_cierre != null ? money(p.valor_cierre) : '—'}</td>
                    <td className="py-2 pr-3 text-xs">{p?.cerrado_at ? new Date(p.cerrado_at).toLocaleDateString(LOCALE) : '—'}</td>
                    <td className="py-2 pr-3 font-mono text-[11px] text-muted-foreground">{p?.hash_cierre?.slice(0, 12) ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">
                      <div className="flex justify-end gap-2">
                        {p?.estado === 'cerrado' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setVer(p)}>Ver foto</Button>
                            <Button size="sm" variant="outline" onClick={async () => {
                              const v = await dlg.pedir('Reabrir mes', [
                                { key: 'motivo', label: '¿Por qué se reabre este mes? Queda escrito en el acta.', multilinea: true, minimo: 5 },
                              ]);
                              if (v) reabrir.mutate({ id: p.id, motivo: v.motivo.trim() });
                            }}>Reabrir</Button>
                          </>
                        )}
                        {p?.estado !== 'cerrado' && !enCurso && (
                          <Button size="sm" onClick={() => setCerrar({ anio, mes })}>Cerrar</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {cerrar && <ModalCerrar {...cerrar} onClose={() => setCerrar(null)} onHecho={() => {
        setCerrar(null); invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-periodos'] });
      }} />}
      {ver && <ModalFoto periodo={ver} onClose={() => setVer(null)} />}
    </div>
  );
}

function ModalCerrar({ anio, mes, onClose, onHecho }: { anio: number; mes: number; onClose: () => void; onHecho: () => void }) {
  const { data: sim, isFetching } = useQuery<{
    lineas: number; valor: number; movimientos_sin_asiento: number; mensaje: string;
  }>({
    queryKey: ['inv-periodo-sim', anio, mes],
    queryFn: () => invRpc('inv_periodo_cerrar', { _anio: anio, _mes: mes, _confirmar: false }),
  });
  const cerrar = useMutation({
    mutationFn: () => invRpc<{ mensaje: string }>('inv_periodo_cerrar', { _anio: anio, _mes: mes, _confirmar: true }),
    onSuccess: (r) => { toast.success(r.mensaje); onHecho(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const bloqueado = (sim?.movimientos_sin_asiento ?? 0) > 0;

  return (
    <Modal title={`Cerrar ${MES[mes]} de ${anio}`} onClose={onClose}>
      <div className="space-y-4">
        {isFetching ? <p className="py-6 text-center text-sm text-muted-foreground">Comprobando…</p> : (
          <>
            <p className="text-sm">{sim?.mensaje}</p>
            {bloqueado ? (
              <p className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
                Cerrar ahora sellaría un descuadre: hay movimientos con valor que nunca llegaron a la
                contabilidad. Resuélvelos primero en <strong>Contabilidad → Excepciones</strong>.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-md border p-3"><span className="block text-xs text-muted-foreground">Líneas a congelar</span>
                  <span className="text-lg font-semibold tabular-nums">{num(sim?.lineas ?? 0, 0)}</span></div>
                <div className="rounded-md border p-3"><span className="block text-xs text-muted-foreground">Valor</span>
                  <span className="text-lg font-semibold tabular-nums">{money(sim?.valor ?? 0)}</span></div>
              </div>
            )}
            <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              Se guarda la valorización producto por producto y bodega por bodega, con su hash. Después,
              ningún movimiento podrá fecharse en {MES[mes]} sin reabrir el mes — y reabrirlo exige
              nivel 5 y un motivo escrito.
            </p>
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={bloqueado || isFetching || cerrar.isPending} onClick={() => cerrar.mutate()}>
            Cerrar el mes
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ModalFoto({ periodo, onClose }: { periodo: Periodo; onClose: () => void }) {
  const [loc, setLoc] = useState('');
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: rows = [], isFetching } = useQuery<LineaVal[]>({
    queryKey: ['inv-periodo-val', periodo.id, loc],
    queryFn: () => invRpc<LineaVal[]>('inv_periodo_valorizacion', { _id: periodo.id, _location: loc || null }),
  });
  const totalSellado = rows.reduce((a, r) => a + Number(r.valor), 0);
  const totalHoy = rows.reduce((a, r) => a + Number(r.valor_hoy), 0);

  return (
    <Modal title={`Valorización congelada · ${MES[periodo.mes]} ${periodo.anio}`} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <Campo label="Bodega" className="w-56">
            <select className={fieldCls} value={loc} onChange={(e) => setLoc(e.target.value)}>
              <option value="">Todas</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <p className="text-xs text-muted-foreground">
            Sellado <strong className="tabular-nums">{money(totalSellado)}</strong> · hoy{' '}
            <strong className="tabular-nums">{money(totalHoy)}</strong>
          </p>
        </div>
        <div className="max-h-[55vh] overflow-auto rounded-md border">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b bg-card text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Bodega</th><th className="px-3 py-2">Producto</th>
                <th className="px-3 py-2 text-right">Unidades</th><th className="px-3 py-2 text-right">Costo</th>
                <th className="px-3 py-2 text-right">Valor sellado</th><th className="px-3 py-2 text-right">Hoy</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="px-3 py-1.5">{r.bodega}</td>
                  <td className="px-3 py-1.5"><span className="font-mono text-xs text-muted-foreground">{r.codigo}</span> {r.producto}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.unidades)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.costo_promedio, 4)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(r.valor)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${Number(r.valor_hoy) !== Number(r.valor) ? 'text-muted-foreground' : ''}`}>
                    {money(r.valor_hoy)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-mono text-[11px] text-muted-foreground">hash {periodo.hash_cierre}</p>
      </div>
    </Modal>
  );
}
